/*============================================================================
  ga4-events.js — GA4 dataLayer events layer (WS1 spec) for GTM-WL2DZNMQ
  Rainger Supply Co · Impulse v8.2.1

  Architecture: ONE deferred IIFE, included from layout/theme.liquid and
  layout/landing.liquid. Every listener is either (a) a document-level
  CustomEvent the theme already dispatches, or (b) event delegation on
  stable selectors. ZERO vendor-file edits — assets/theme.js is untouched.

  Conventions (GA4):
  - push({ ecommerce: null }) before every ecommerce event (GA4 reset rule)
  - Shopify cart JSON prices are CENTS — divide by 100
  - item_id = variant SKU, fallback String(variant_id); collection-grid
    select_item uses the product id (cards don't expose variant SKUs)
  - item_name = product title
  - currency = cart presentment currency from /cart.js ('AUD' until the
    first fetch resolves) — future Markets/multi-currency safe

  Events implemented here:
    add_to_cart                 F1.3 'ajaxProduct:added' + F1.4
                                'product-form:submit:success' (500ms dedupe);
                                quantity = submitting form's input, else
                                snapshot delta, else response quantity
                                + F1.6 cart:updated positive deltas
    view_cart                   F1.5 'drawerOpen.CartDrawer' + cart-page load
    remove_from_cart            F1.6 cart:updated negative deltas +
                                delegated click on '.cart__remove a'
    remove_from_cart_untracked  F1.6 snapshot-miss fallback (custom event,
                                NO ecommerce block — keeps item reports clean)
    begin_checkout              F1.7 standard/express/buy-now; buy-now builds
                                items from the enclosing product form
    checkout_blocked_backorder  F1.7 cart-page backorder gate diagnostic
    backorder_acknowledge       F1.8 '#backorder-acknowledge' change
    phone_call_click            F1.9 delegated 'a[href^="tel:"]'
    lander_cta_click /          F1.11 lander CTAs — gated on body class
    shipping_info_click               'template-landing' (theme homepage
                                      heroes also use .hero a.btn)
    calc_vehicle_select /       F1.12 payload calculators (AU + US) — matched
    calc_compare_add /                on '.rs-variant-card' etc.: the tools
    calc_compare_view /               re-render synchronously inside their own
    calc_towing_toggle /              click handlers, so descendant selectors
    calc_licence_toggle               like '#rsVariantCards button' can never
                                      match the detached target at document
                                      level — only the element's OWN class can
    search_open / search        F1.13 'predictive-search:open' + delegated
                                      submit on the search form
    select_item / filter_apply  F1.14 '.grid-product__link' clicks +
                                      'collection:reloaded'
    review_stars_click          F1.15 '.rs-loox-rating--link' (live PDP stars)
    email_click                 F1.16 'a[href^="mailto:"]' (admin content)

  Known, accepted limitations (documented for the GTM workstream):
  - Express/dynamic checkout buttons render inside cross-origin iframes —
    clicks in there NEVER reach this document, so begin_checkout
    checkout_type:'express' systematically undercounts. Only clicks on
    interactive children rendered in the host page fire. GA4 purchase
    attribution (F1.17, Google channel integration) covers the truth.
  - checkout_blocked_backorder: when the gate disables the checkout button,
    clicks landing exactly ON the disabled button emit nothing in Firefox
    (it suppresses all pointer/click events on disabled controls). Accepted:
    Chrome-majority capture; the capture-phase click + pointerdown pair
    below catches everything else.
  - Theme-editor sessions are excluded entirely (Shopify.designMode guard).

  Liquid-side pushes (NOT in this file):
    view_item      snippets/ga4-view-item.liquid (F1.2) — rendered from
                   sections/main-product.liquid AND
                   sections/main-product-high-variant.liquid
    generate_lead  snippets/ga4-generate-lead.liquid (F1.10) — rendered from
                   sections/contact-form.liquid,
                   snippets/footer-newsletter.liquid (the LIVE footer path)
                   and snippets/newsletter-form.liquid (popup/password page)

  Reversal: remove the two script tags in the layouts + delete this asset.
==============================================================================*/
(function () {
  'use strict';

  // Theme-editor (customiser) sessions must not push events into the live
  // GTM container — there is no dev/staging container to divert to.
  if (window.Shopify && window.Shopify.designMode) return;

  if (window.__rsGa4Events) return; // double-load guard
  window.__rsGa4Events = true;

  window.dataLayer = window.dataLayer || [];

  // Presentment currency — updated from every /cart.js response so a future
  // Shopify Markets enablement can't mislabel foreign-currency values as AUD.
  var currencyCode = 'AUD';

  /* ── helpers ────────────────────────────────────────────────────────── */

  function push(obj) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(obj);
  }

  // GA4 convention: null out ecommerce before each ecommerce event so stale
  // items from a previous push can never leak into the next tag fire.
  function pushEcommerce(eventName, ecommerce, extraParams) {
    push({ ecommerce: null });
    var payload = { event: eventName, ecommerce: ecommerce };
    if (extraParams) {
      for (var key in extraParams) {
        if (Object.prototype.hasOwnProperty.call(extraParams, key)) {
          payload[key] = extraParams[key];
        }
      }
    }
    push(payload);
  }

  // Shopify cart JSON prices are cents.
  function cents(value) {
    return Math.round(Number(value) || 0) / 100;
  }

  // GA4 item from a Shopify cart line (/cart.js items[] or /cart/add.js
  // response — both carry sku, variant_id, product_title, variant_title,
  // price [cents], quantity).
  function itemFromLine(line, quantityOverride) {
    var item = {
      item_id: line.sku ? String(line.sku) : String(line.variant_id),
      item_name: line.product_title || line.title || '',
      price: cents(line.price),
      quantity: quantityOverride != null ? quantityOverride : (line.quantity || 1)
    };
    if (line.variant_title) item.item_variant = line.variant_title;
    return item;
  }

  function itemsValue(items) {
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      total += (items[i].price || 0) * (items[i].quantity || 1);
    }
    return Math.round(total * 100) / 100;
  }

  function closestFrom(target, selector) {
    if (!target || typeof target.closest !== 'function') return null;
    return target.closest(selector);
  }

  function cleanText(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  /* ── cached cart snapshot (fuels diffs + begin_checkout payloads) ────── */

  var cartSnapshot = null;
  var snapshotVersion = 0;

  function setSnapshot(cart) {
    if (cart && cart.items) {
      cartSnapshot = cart;
      snapshotVersion++;
      if (cart.currency) currencyCode = cart.currency;
    }
  }

  function fetchCart() {
    var versionAtRequest = snapshotVersion;
    return window.fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        // Don't clobber a snapshot that cart:updated refreshed with newer
        // data while this request was in flight.
        if (snapshotVersion === versionAtRequest) setSnapshot(cart);
        return cart;
      })
      .catch(function () { return null; });
  }

  /* ── add_to_cart dedupe ────────────────────────────────────────────────
     F1.3 vs F1.4: 500ms token keyed on variant_id (both see the same
     /cart/add.js response). The cart:updated diff path checks a wider 10s
     window keyed on line key AND variant id — see F1.6a for why. */

  var addReportedAt = {};

  function markAddReported(id) {
    if (id != null) addReportedAt[String(id)] = Date.now();
  }

  function addReportedWithin(id, windowMs) {
    var at = addReportedAt[String(id)];
    return typeof at === 'number' && (Date.now() - at) < windowMs;
  }

  function markAddFromResponse(product) {
    markAddReported(product.variant_id);
    if (product.key) markAddReported(product.key);
  }

  function pushAddToCart(items) {
    pushEcommerce('add_to_cart', {
      currency: currencyCode,
      value: itemsValue(items),
      items: items
    });
  }

  // Quantity actually ADDED by this action — /cart/add.js reports the
  // post-merge line total when the variant is already in the cart, so the
  // raw response quantity can overcount repeat adds. Order of trust:
  //   (a) the submitting form's [name="quantity"] input — the only source
  //       that is unambiguously "what this click added"
  //   (b) delta vs the cached snapshot for the same line key (floor 1)
  //   (c) the response quantity (fresh line — nothing merged)
  function addedQuantity(detail, product) {
    var btn = detail && detail.addToCartBtn;
    var form = btn && typeof btn.closest === 'function' ? btn.closest('form') : null;
    var input = form ? form.querySelector('[name="quantity"]') : null;
    if (input) {
      var fromForm = parseInt(input.value, 10);
      if (fromForm > 0) return fromForm;
    }
    if (cartSnapshot && cartSnapshot.items && product.key) {
      for (var i = 0; i < cartSnapshot.items.length; i++) {
        if (cartSnapshot.items[i].key === product.key) {
          return Math.max(1, (product.quantity || 1) - cartSnapshot.items[i].quantity);
        }
      }
    }
    return product.quantity || 1;
  }

  /* ── F1.3 — add_to_cart via the legacy dispatcher ─────────────────────
     Covers PDP buy box, quick-shop and featured-product: they all funnel
     through theme.js ProductForm, which dispatches 'ajaxProduct:added'
     (bubbles to document) with detail.product = the /cart/add.js response.
     CAVEAT: the 422 error path (and main-product-high-variant's re-dispatch)
     fires the SAME event name with NO detail — guard before touching it. */
  document.addEventListener('ajaxProduct:added', function (e) {
    if (!e.detail || !e.detail.product) return; // 422/error dispatch — ignore
    var product = e.detail.product;
    if (addReportedWithin(product.variant_id, 500)) return; // dedupe vs F1.4
    markAddFromResponse(product);
    pushAddToCart([itemFromLine(product, addedQuantity(e.detail, product))]);
    // Refresh the snapshot so later diffs / begin_checkout payloads don't
    // work from pre-add data (fire-and-forget).
    fetchCart();
  });

  /* ── F1.4 — parallel modern hook (flex-pdp / hot-reload surfaces) ─────
     detail.response = /cart/add.js response, detail.cart = full cart after
     submission. 500ms token dedupes against F1.3 for any surface that ends
     up wired to both paths. Quantity computed BEFORE the snapshot is
     replaced so the delta path still has the pre-add baseline. */
  document.addEventListener('product-form:submit:success', function (e) {
    var detail = e.detail || {};
    var product = detail.response;
    var quantity = (product && product.variant_id) ? addedQuantity(detail, product) : null;
    if (detail.cart) setSnapshot(detail.cart);
    if (!product || !product.variant_id) return;
    if (addReportedWithin(product.variant_id, 500)) return; // dedupe vs F1.3
    markAddFromResponse(product);
    pushAddToCart([itemFromLine(product, quantity)]);
  });

  /* ── F1.5 — view_cart: drawer open (theme announces it) + cart page ─── */

  function pushViewCart(cart) {
    if (!cart || !cart.items) return;
    pushEcommerce('view_cart', {
      currency: currencyCode,
      value: cents(cart.total_price),
      items: cart.items.map(function (line) { return itemFromLine(line); })
    });
  }

  document.addEventListener('drawerOpen.CartDrawer', function () {
    fetchCart().then(pushViewCart);
  });

  /* ── F1.6a — cart:updated diffed against the cached snapshot ──────────
     Repo evidence: 'cart:updated' is dispatched from exactly ONE site,
     theme.js quantityChanged() — the /cart/change.js qty-stepper path. No
     add path dispatches it, and F1.3 refreshes the snapshot right after an
     add, so fresh adds can't reappear here as deltas. The 10s suppression
     window (keyed per line key + variant id, marked ONLY by F1.3/F1.4) is
     cheap insurance for a future surface that both adds and dispatches
     cart:updated. The diff path deliberately does NOT mark — consecutive
     genuine stepper increments must each report. */
  document.addEventListener('cart:updated', function (e) {
    var newCart = e.detail && e.detail.cart;
    if (!newCart || !newCart.items) return;
    var oldCart = cartSnapshot;
    setSnapshot(newCart);
    if (!oldCart || !oldCart.items) return; // no baseline yet — just cache

    var oldByKey = {};
    oldCart.items.forEach(function (line) { oldByKey[line.key] = line; });
    var newByKey = {};
    newCart.items.forEach(function (line) { newByKey[line.key] = line; });

    var addedLines = [];
    var removedLines = [];

    newCart.items.forEach(function (line) {
      var before = oldByKey[line.key];
      var delta = line.quantity - (before ? before.quantity : 0);
      if (delta > 0) addedLines.push({ line: line, qty: delta });
      if (delta < 0) removedLines.push({ line: line, qty: -delta });
    });
    oldCart.items.forEach(function (line) {
      if (!newByKey[line.key]) {
        removedLines.push({ line: line, qty: line.quantity });
      }
    });

    addedLines = addedLines.filter(function (entry) {
      return !(addReportedWithin(entry.line.key, 10000) ||
               addReportedWithin(entry.line.variant_id, 10000));
    });

    if (addedLines.length) {
      pushAddToCart(addedLines.map(function (entry) {
        return itemFromLine(entry.line, entry.qty);
      }));
    }
    if (removedLines.length) {
      var removedItems = removedLines.map(function (entry) {
        return itemFromLine(entry.line, entry.qty);
      });
      pushEcommerce('remove_from_cart', {
        currency: currencyCode,
        value: itemsValue(removedItems),
        items: removedItems
      });
    }
  });

  /* ── F1.6b — Remove link is plain navigation (no ajax, no cart:updated)
     — push synchronously before the page unloads. dataLayer.push is
     synchronous so the entry lands even mid-navigation. */
  document.addEventListener('click', function (e) {
    var link = closestFrom(e.target, '.cart__remove a');
    if (!link) return;
    var match = /[?&]id=([^&]+)/.exec(link.getAttribute('href') || '');
    var lineKey = match ? decodeURIComponent(match[1]) : null;
    var line = null;
    if (lineKey && cartSnapshot && cartSnapshot.items) {
      for (var i = 0; i < cartSnapshot.items.length; i++) {
        if (cartSnapshot.items[i].key === lineKey) {
          line = cartSnapshot.items[i];
          break;
        }
      }
    }
    if (line) {
      var item = itemFromLine(line);
      pushEcommerce('remove_from_cart', {
        currency: currencyCode,
        value: itemsValue([item]),
        items: [item]
      });
    } else {
      // Snapshot miss (/cart.js fetch still in flight). Do NOT emit a
      // degenerate ecommerce event with items:[] — GA4 item-scoped remove
      // reports would collect unattributable rows. A plain custom event
      // keeps the funnel count honest without polluting ecommerce data.
      push({ event: 'remove_from_cart_untracked', line_key: lineKey || 'unknown' });
    }
  });

  /* ── F1.7 — begin_checkout (3 surfaces) + backorder-gate diagnostic ─── */

  function backorderGateWillBlock() {
    var ackBlock = document.getElementById('backorder-acknowledgment-block');
    var checkbox = document.getElementById('backorder-acknowledge');
    return !!(ackBlock && checkbox && ackBlock.style.display !== 'none' && !checkbox.checked);
  }

  var lastBlockedPushAt = 0;
  function pushCheckoutBlocked() {
    var now = Date.now();
    if (now - lastBlockedPushAt < 1000) return; // capture/pointerdown overlap
    lastBlockedPushAt = now;
    var payload = { event: 'checkout_blocked_backorder' };
    if (cartSnapshot) payload.cart_value = cents(cartSnapshot.total_price);
    push(payload);
  }

  function pushBeginCheckout(checkoutType, items, value) {
    var ecommerce = { currency: currencyCode, items: items };
    if (value != null) ecommerce.value = value;
    pushEcommerce('begin_checkout', ecommerce, { checkout_type: checkoutType });
  }

  function cartItems() {
    if (!cartSnapshot || !cartSnapshot.items) return [];
    return cartSnapshot.items.map(function (line) { return itemFromLine(line); });
  }

  // Buy It Now bypasses the cart entirely — the checkout contains ONLY the
  // enclosing product form's variant/qty, so the cart snapshot would be the
  // wrong payload (empty for a first-touch buyer, or someone else's items).
  // Price comes from the view_item payload stashed by ga4-view-item.liquid
  // when the form's selected variant matches; otherwise item_id/quantity
  // only — omitting price beats reporting a wrong one.
  function buyNowItems(button) {
    var form = closestFrom(button, 'form');
    var idInput = form ? form.querySelector('[name="id"]') : null;
    var variantId = idInput && idInput.value ? String(idInput.value) : '';
    var qtyInput = form ? form.querySelector('[name="quantity"]') : null;
    var quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    var stored = window.__rsGa4ViewItem;
    if (stored && stored.item && variantId && String(stored.variant_id) === variantId) {
      var item = {};
      for (var key in stored.item) {
        if (Object.prototype.hasOwnProperty.call(stored.item, key)) {
          item[key] = stored.item[key];
        }
      }
      item.quantity = quantity;
      return [item];
    }
    if (!variantId) return [];
    return [{ item_id: variantId, quantity: quantity }];
  }

  document.addEventListener('click', function (e) {
    var target = e.target;
    var standard = closestFrom(target, 'button[name="checkout"]');
    // Interactive children only: the wallet buttons themselves live in
    // cross-origin iframes (untrackable — see header), and matching the
    // whole container would fire false positives on padding clicks.
    var express = closestFrom(target,
      '.additional-checkout-buttons button, .additional-checkout-buttons [role="button"]');
    var buyNow = closestFrom(target, '.shopify-payment-button__button');
    if (!standard && !express && !buyNow) return;

    // Cart-page backorder gate (sections/main-cart.liquid) preventDefault()s
    // the submit when un-acknowledged — report the block, not a false start.
    if (closestFrom(target, '#CartPageForm') && backorderGateWillBlock()) {
      pushCheckoutBlocked();
      return;
    }

    if (buyNow) {
      var items = buyNowItems(buyNow);
      var value = itemsValue(items);
      pushBeginCheckout('buy_now_pdp', items, value > 0 ? value : null);
      return;
    }
    pushBeginCheckout(express ? 'express' : 'standard', cartItems(),
      cartSnapshot ? cents(cartSnapshot.total_price) : 0);
  });

  // Blocked-attempt capture. When the gate blocks, main-cart's script sets
  // disabled on the checkout button (disabled controls swallow clicks — the
  // bubble listener above can never see them) and pointer-events:none on the
  // express area (those clicks RETARGET to the wrapper, which matches none
  // of the three selectors above). The wrapper itself is never disabled, so
  // a document-level CAPTURE-phase listener sees every click that resolves
  // to it or anything inside it. Known gap: a click landing exactly on the
  // disabled button emits NO event at all in Firefox (it suppresses events
  // on disabled controls) — accepted limitation, Chrome-majority capture.
  document.addEventListener('click', function (e) {
    if (!closestFrom(e.target, '#CartPageForm .cart__checkout-wrapper')) return;
    if (backorderGateWillBlock()) pushCheckoutBlocked();
  }, true);

  // Fallback: Chrome dispatches pointerdown even when the hit-test target is
  // the disabled button itself — catches the direct-on-button case the click
  // capture can't see. Shares the 1s dedupe with the capture listener.
  document.addEventListener('pointerdown', function (e) {
    var wrapper = closestFrom(e.target, '#CartPageForm .cart__checkout-wrapper');
    if (!wrapper) return;
    if (backorderGateWillBlock()) pushCheckoutBlocked();
  });

  /* ── F1.8 — backorder_acknowledge (high-AOV friction signal) ──────────
     Delegated change listener — the existing handler in main-cart.liquid
     is not touched. */
  document.addEventListener('change', function (e) {
    var target = e.target;
    if (!target || target.id !== 'backorder-acknowledge') return;
    var payload = { event: 'backorder_acknowledge', acknowledged: !!target.checked };
    if (cartSnapshot) payload.cart_value = cents(cartSnapshot.total_price);
    push(payload);
  });

  /* ── F1.9 — phone_call_click (phone-led business → GA4 key event) ─────
     One delegated listener covers theme pages, landers, the footer setting
     and admin-pasted content (range page). */
  document.addEventListener('click', function (e) {
    var link = closestFrom(e.target, 'a[href^="tel:"]');
    if (!link) return;
    var locationHint = closestFrom(link, '[class]');
    push({
      event: 'phone_call_click',
      link_text: cleanText(link.textContent).slice(0, 80),
      page_path: window.location.pathname,
      link_location: locationHint ? String(locationHint.className).trim().slice(0, 60) : ''
    });
  });

  /* ── F1.11 — lander CTAs + shipping-info links (ad-traffic pages) ─────
     layout/landing.liquid loads this asset too. Gated on the landing body
     class because the theme's own homepage hero sections also use
     '.hero a.btn' — without the gate those would fire mislabelled
     lander_cta_click events. tel: links are excluded here: F1.9 already
     reports them as phone_call_click (one click, one event). */
  document.addEventListener('click', function (e) {
    if (!document.body || !document.body.classList.contains('template-landing')) return;

    var ship = closestFrom(e.target, '.ship-link, .rx-ship-bar');
    if (ship) {
      push({ event: 'shipping_info_click', page_path: window.location.pathname });
      return;
    }

    var cta = closestFrom(e.target, '.buy-bar a, .topbar a.btn, .hero a.btn');
    if (!cta) return;
    var href = cta.getAttribute('href') || '';
    if (/^tel:/i.test(href)) return; // phone_call_click owns these
    push({
      event: 'lander_cta_click',
      destination: href.slice(0, 200),
      link_text: cleanText(cta.textContent).slice(0, 80),
      page_path: window.location.pathname
    });
  });

  /* ── F1.12 — payload calculators (remarketing-audience engine) ────────
     AU tool (assets/payload-calculator.js) + US tool
     (assets/us-trucks-payload-calculator.js). Their controls rebind on
     every render, so only document-level delegation survives. calc_variant
     distinguishes the tools via the US page scope class .rs-ustruck-page.

     IMPORTANT: the vehicle cards are matched on the button's OWN class
     ('.rs-variant-card' — rendered by both tools), NOT a descendant
     selector like '#rsVariantCards button'. The calculators' own click
     handlers synchronously re-render the container (innerHTML = ...)
     BEFORE the event bubbles to document, so by the time this listener
     runs the clicked button is detached — matches() on its own class still
     works on a detached node; a descendant selector never matches. */

  function calcVariant() {
    return document.querySelector('.rs-ustruck-page') ? 'us' : 'au';
  }

  document.addEventListener('click', function (e) {
    var target = e.target;

    var vehicleBtn = closestFrom(target, '.rs-variant-card');
    if (vehicleBtn) {
      var label = vehicleBtn.querySelector('strong'); // works on detached subtree
      push({
        event: 'calc_vehicle_select',
        calc_variant: calcVariant(),
        vehicle_label: cleanText((label ? label.textContent : vehicleBtn.textContent)).slice(0, 80)
      });
      return;
    }

    if (closestFrom(target, '#rsShowCompare')) {
      push({ event: 'calc_compare_view', calc_variant: calcVariant() });
      return;
    }

    var licenceOpt = closestFrom(target, '.rs-licence-opt');
    if (licenceOpt) {
      push({
        event: 'calc_licence_toggle',
        calc_variant: 'us',
        licence_class: licenceOpt.getAttribute('data-licence') || ''
      });
    }
  });

  document.addEventListener('change', function (e) {
    var target = e.target;
    if (!target || typeof target.matches !== 'function') return;

    if (target.matches('#rsTableBody input[type="checkbox"][data-vid]')) {
      // The calculators' own element-level change handler runs first and,
      // on a 5th selection, synchronously unchecks the box (max-4 alert).
      // By the time this document-level listener reads the state, a
      // rejected add and a genuine deselect are indistinguishable — so
      // checked:false is labelled 'remove_or_rejected' rather than
      // pretending it was a clean removal.
      push({
        event: 'calc_compare_add',
        calc_variant: calcVariant(),
        vehicle_id: target.getAttribute('data-vid') || '',
        action: target.checked ? 'add' : 'remove_or_rejected'
      });
      return;
    }

    if (target.id === 'rsTowing') {
      push({
        event: 'calc_towing_toggle',
        calc_variant: calcVariant(),
        enabled: !!target.checked
      });
    }
  });

  /* ── F1.13 — search: predictive open + submitted query ────────────────
     theme.js dispatches 'predictive-search:open' (bubbles to document).
     The theme's only search form is snippets/predictive-search.liquid
     (<form action="{{ routes.search_url }}" method="get" role="search">) —
     matched structurally so a future second search surface is covered. */
  document.addEventListener('predictive-search:open', function () {
    push({ event: 'search_open' });
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || typeof form.matches !== 'function') return;
    if (!form.matches('form[role="search"], form[action*="/search"]')) return;
    var input = form.querySelector('input[name="q"]');
    var term = input ? cleanText(input.value) : '';
    if (!term) return;
    push({ event: 'search', search_term: term.slice(0, 100) });
  });

  /* ── F1.14 — collection grids: select_item + filter_apply ─────────────
     Cards render via snippets/product-grid-item.liquid: the '.grid-product'
     wrapper carries data-product-handle/-id; delegation survives the ajax
     grid re-render ('collection:reloaded', theme.js). item_id here is the
     product id (grid cards don't expose variant SKUs); handle rides along
     as a top-level param for GTM joins. Predictive-search result links
     (same class, no .grid-product wrapper) fall back to the href handle. */
  document.addEventListener('click', function (e) {
    var link = closestFrom(e.target, '.grid-product__link');
    if (!link) return;
    var card = closestFrom(link, '.grid-product');
    var handle = (card && card.getAttribute('data-product-handle')) || '';
    if (!handle) {
      var hrefMatch = /\/products\/([^/?#]+)/.exec(link.getAttribute('href') || '');
      if (hrefMatch) handle = hrefMatch[1];
    }
    var productId = card ? card.getAttribute('data-product-id') : null;
    var titleEl = card ? card.querySelector('.grid-product__title') : null;
    var collectionMatch = /\/collections\/([^/?#]+)/.exec(window.location.pathname);
    pushEcommerce('select_item', {
      item_list_name: collectionMatch ? collectionMatch[1] : 'product_grid',
      items: [{
        item_id: productId ? String(productId) : handle,
        item_name: titleEl ? cleanText(titleEl.textContent) : handle
      }]
    }, { product_handle: handle });
  });

  // Fires on every ajax grid renew (filters, sort, pagination) —
  // filter_params carries the live query string so GTM can distinguish.
  document.addEventListener('collection:reloaded', function () {
    push({ event: 'filter_apply', filter_params: window.location.search.slice(0, 500) });
  });

  /* ── F1.15 — Loox star rating click (live store-wide) ─────────────────
     snippets/product-template.liquid renders 'a.rs-loox-rating--link'
     under the price on every rated PDP → /pages/reviews. */
  document.addEventListener('click', function (e) {
    if (!closestFrom(e.target, '.rs-loox-rating--link')) return;
    push({ event: 'review_stars_click', page_path: window.location.pathname });
  });

  /* ── F1.16 — mailto clicks (zero repo instances today; covers admin-
     pasted content like the range page's "message us for a quote") ────── */
  document.addEventListener('click', function (e) {
    var link = closestFrom(e.target, 'a[href^="mailto:"]');
    if (!link) return;
    push({
      event: 'email_click',
      link_text: cleanText(link.textContent).slice(0, 80),
      page_path: window.location.pathname
    });
  });

  /* ── init — establish the cart snapshot; view_cart on the cart page ─── */

  function init() {
    var onCartPage = document.body && document.body.classList.contains('template-cart');
    if (onCartPage) {
      fetchCart().then(pushViewCart); // F1.5 — cart-page load
    } else {
      fetchCart(); // snapshot only — fuels diffs + begin_checkout payloads
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
